// Client-side image compression before upload.
// - Resizes to fit within maxWidth x maxHeight keeping aspect ratio
// - Re-encodes as JPEG (smaller than PNG for photos) unless source is PNG with transparency
// - Returns a File so the backend still sees a standard multipart upload

const DEFAULTS = { maxWidth: 1280, maxHeight: 1280, quality: 0.78, maxSizeMB: 1.2 };

const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const loadImage = (dataUrl) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = dataUrl;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
  canvas.toBlob((b) => resolve(b), type, quality);
});

export async function compressImage(file, opts = {}) {
  if (!file) return file;
  const { maxWidth, maxHeight, quality, maxSizeMB } = { ...DEFAULTS, ...opts };
  if (!file.type || !file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file; // keep animated gifs as-is

  try {
    const dataUrl = await readAsDataURL(file);
    const img = await loadImage(dataUrl);
    let { width, height } = img;
    const scale = Math.min(1, maxWidth / width, maxHeight / height);
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    // Use JPEG for photos (much smaller). Keep PNG only if source is PNG with transparency.
    const outputType = file.type === "image/png" ? "image/jpeg" : "image/jpeg";
    let q = quality;
    let blob = await canvasToBlob(canvas, outputType, q);

    // If still too big, reduce quality iteratively
    while (blob && blob.size > maxSizeMB * 1024 * 1024 && q > 0.4) {
      q -= 0.1;
      blob = await canvasToBlob(canvas, outputType, q);
    }
    if (!blob) return file;

    const newName = file.name.replace(/\.(png|webp|heic|heif|jpg|jpeg)$/i, "") + ".jpg";
    return new File([blob], newName, { type: outputType, lastModified: Date.now() });
  } catch {
    return file; // fallback to original if anything fails
  }
}

export function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
